import { useEffect } from 'react';

const SITE_URL = 'https://coloradospringsadultkarate.com';
const DEFAULT_OG = 'https://customer-assets.emergentagent.com/job_314e0372-4394-4228-b1d6-7dd75615009a/artifacts/ie6smq8s_kempo_patch_HQ_large.png';

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeNode(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * Sets per-page metadata: title, description, canonical, OG/Twitter, optional JSON-LD
 * schemas (array of {id, data}). Automatically appends a BreadcrumbList schema when
 * `breadcrumbs` are supplied.
 */
export function usePageMeta({
  title,
  description,
  path = '/',
  ogImage = DEFAULT_OG,
  schemas = [],
  breadcrumbs,
  noindex = false,
}) {
  useEffect(() => {
    const canonical = SITE_URL + (path.startsWith('/') ? path : `/${path}`);
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
    }
    upsertLink('canonical', canonical);

    // Robots
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex, nofollow' : 'index, follow',
    });

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title || '' });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description || '' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: 'Universal Kempo Karate',
    });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title || '' });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description || '' });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    // Breadcrumbs schema
    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 1) {
      upsertJsonLd('page-breadcrumbs-jsonld', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.label,
          item: b.href ? SITE_URL + b.href : undefined,
        })),
      });
    } else {
      removeNode('page-breadcrumbs-jsonld');
    }

    // Page-level JSON-LD schemas (removed on unmount)
    const addedIds = [];
    schemas.forEach(({ id, data }) => {
      upsertJsonLd(id, data);
      addedIds.push(id);
    });

    return () => {
      addedIds.forEach(removeNode);
      // Keep breadcrumbs tidy across navigations
      removeNode('page-breadcrumbs-jsonld');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, ogImage, JSON.stringify(schemas), JSON.stringify(breadcrumbs), noindex]);
}

export const SITE = { url: SITE_URL, defaultOg: DEFAULT_OG };
